// Multi-asset fetch for O/D Analytics — trips (GET /v3/trip) and, behind
// OD_TELEMETRY_ENGINES_ENABLED, driving events + idle episodes (GET /v3/trip/analytics).
//
// GET /v3/trip takes a single mandatory deviceOid, so a fleet view means one request per
// asset. This mirrors the proven server-side pattern in
// report-service/modules/automateReportOdDispatch.js, which fans out the same endpoint
// per device at AUTOMATE_REPORT_INSIGHTS_OD_FETCH_CONCURRENCY (default 5).
//
// Two response quirks this module exists to absorb:
//
//  1. APIGetTripReport catches with `(error) => error`, so a network or HTTP failure comes
//     back as an AxiosError object in the success position. Checking `.status` on it is
//     meaningless.
//  2. The gRPC gateway answers HTTP 200 with `{ status: 4xx }` in the body, so the real
//     status lives in the envelope, not the HTTP code.
//
// Without both checks every failure — including the server's 31-day period cap — collapses
// into a generic "no data found", which is what the old panel did.

import { APIGetTripAnalytics, APIGetTripReport, APIGetTripTrail } from '../../../config/restAPI/FetchAPI';

export const OD_MAX_ASSETS = 10;
export const OD_FETCH_CONCURRENCY = 5;

const GENERIC_ERROR = 'Unable to retrieve trips for this asset.';

/** Pull the most specific message the response or error carries. */
const extractErrorMessage = (result) => {
    const envelopeMessage = result?.data?.message;
    if (typeof envelopeMessage === 'string' && envelopeMessage.trim() !== '') return envelopeMessage;

    const httpMessage = result?.response?.data?.message;
    if (typeof httpMessage === 'string' && httpMessage.trim() !== '') return httpMessage;

    if (result instanceof Error && result.message) return result.message;
    return GENERIC_ERROR;
};

/**
 * Normalise one APIGetTripReport result into { trips, tripsPerHour, totals, error }.
 * Exported for testing and so callers never have to know about the two quirks above.
 */
export const normaliseTripResponse = (result) => {
    if (result instanceof Error || !result?.data) {
        return {
            trips: [], totals: null, ghostTrips: 0, error: extractErrorMessage(result),
        };
    }

    // Envelope status, not HTTP status — the gateway returns 200 with a 4xx body.
    if (result.data.status !== 200) {
        return {
            trips: [], totals: null, ghostTrips: 0, error: extractErrorMessage(result),
        };
    }

    const payload = result.data.data;
    if (!payload || !Array.isArray(payload.tripList)) {
        return {
            trips: [], totals: null, ghostTrips: 0, error: null,
        };
    }

    return {
        trips: payload.tripList,
        totals: {
            totalDistance: payload.totalDistance,
            totalDuration: payload.totalDuration,
            totalEnergy: payload.totalEnergy,
            avgDistance: payload.avgDistance,
            avgDuration: payload.avgDuration,
        },
        // Journeys below the vehicle preset's minimum trip distance. They are excluded from
        // `tripList` by the status selection and from every figure derived from it, so the count is
        // the only thing that explains a journey the operator remembers but cannot find.
        // Absent on a server that predates the derived-trip work, which reads as zero.
        ghostTrips: payload.ghostTrips?.total ?? 0,
        error: null,
    };
};

/** The assets worth fanning out over, capped. */
const requestQueue = (assets) => (Array.isArray(assets) ? assets : [])
    .filter((asset) => asset?.uuid)
    .slice(0, OD_MAX_ASSETS);

/** Run `worker` over `items` OD_FETCH_CONCURRENCY at a time, results in the order given. */
const mapWithConcurrency = async (items, worker) => {
    const results = new Array(items.length);
    let cursor = 0;

    const runNext = async () => {
        while (cursor < items.length) {
            const index = cursor;
            cursor += 1;
            // Sequential await is the point: each worker takes the next item only once its
            // current request settles, which is what bounds concurrency to the pool size.
            // Firing these in parallel would defeat the cap.
            // eslint-disable-next-line no-await-in-loop
            results[index] = await worker(items[index], index);
        }
    };

    await Promise.all(
        Array.from({ length: Math.min(OD_FETCH_CONCURRENCY, items.length) }, runNext),
    );

    return results;
};

/**
 * Fetch trips for up to OD_MAX_ASSETS assets, OD_FETCH_CONCURRENCY at a time.
 *
 * Resolves to one entry per asset in the order given — never rejects. A single failing
 * asset keeps its entry with an `error` so the Overview table can show the reason inline
 * instead of the whole page failing.
 *
 * @param {Array<{uuid: string, id: string, name: string}>} assets
 * @param {{token: string, agencyId: string, fromTime: string, toTime: string,
 *          onProgress?: (done: number, total: number) => void}} options
 * @returns {Promise<Array<{asset: object, trips: Array, totals: object|null, error: string|null}>>}
 */
export const fetchTripsForAssets = async (assets, options) => {
    const {
        token, agencyId, fromTime, toTime, onProgress,
    } = options || {};

    const queue = requestQueue(assets);
    if (queue.length === 0) return [];

    let done = 0;

    return mapWithConcurrency(queue, async (asset) => {
        let entry;
        try {
            const raw = await APIGetTripReport(() => {}, token, agencyId, fromTime, toTime, asset.uuid);
            entry = { asset, ...normaliseTripResponse(raw) };
        } catch (error) {
            entry = {
                asset, trips: [], totals: null, error: extractErrorMessage(error),
            };
        }

        done += 1;
        if (typeof onProgress === 'function') onProgress(done, queue.length);
        return entry;
    });
};

// ---- driving events + idle episodes (GET /v3/trip/analytics) ----------------------

const EMPTY_ANALYTICS = {
    events: [],
    eventsAvailability: null,
    episodes: [],
    policy: null,
    policyByAsset: null,
    rollup: null,
    idleAvailability: null,
    fuelTrips: [],
    fuelEvents: [],
    fuelAvailability: null,
    chargeSessions: [],
    chargeAvailability: null,
    ledgerEntries: [],
    ledgerAssets: [],
    ledgerAvailability: null,
    ledgerRollup: null,
    speedTrips: [],
    tooLarge: false,
};

/**
 * Normalise one APIGetTripAnalytics result into the two view payloads.
 *
 * Same two quirks as the trip fetch, plus a third: 413 is not a retry. The window is
 * wider than the server's cap and `message` names it, so it is kept apart from a generic
 * failure — "narrow the window" is a different instruction from "try again".
 */
export const normaliseAnalyticsResponse = (result) => {
    if (result instanceof Error || !result?.data) {
        return { ...EMPTY_ANALYTICS, error: extractErrorMessage(result) };
    }

    const { status } = result.data;

    if (status === 413) {
        return { ...EMPTY_ANALYTICS, error: extractErrorMessage(result), tooLarge: true };
    }

    // No telemetry at all in this window. Not a failure — but not "clean driving" either,
    // so the signal reads as absent and the views say so instead of showing zeros.
    if (status === 204) {
        return {
            ...EMPTY_ANALYTICS, eventsAvailability: 'absent', idleAvailability: 'absent', error: null,
        };
    }

    if (status !== 200) {
        return { ...EMPTY_ANALYTICS, error: extractErrorMessage(result) };
    }

    const payload = result.data.data || {};
    const events = payload.events || {};
    const idle = payload.idle || {};
    // `fuel` is absent on a server that predates the reconciled-fuel work; the empty array makes
    // every consumer fall back to the trip list's own totalFuelLitre rather than blanking the
    // column. `fuel.events` carries the refill and drain detections — the Overview's Drains column
    // reads the DRAIN half. `fuel.assets` and `fuel.rollup` are still part of the contract with
    // nothing rendering them; add them here when a Fuel view needs them.
    const fuel = payload.fuel || {};
    // Absent on a server that predates the true-peak work; the Dayboard then shows "—" rather than
    // silently falling back to the fastest safety event, which is the number this replaced.
    const speed = payload.speed || {};
    // Absent on a server that predates the charge detector. Its availability is its own: an ICE
    // fleet reads `unpopulated` here while its litres read `available`.
    const charge = payload.charge || {};
    // Absent on a server that predates the merge. The Ledger view then reads as "no signal" rather
    // than joining `fuel.events` and `charge.sessions` itself — which is precisely the fourth copy
    // of ledger logic this section exists to stop the client writing.
    const ledger = payload.ledger || {};

    return {
        events: Array.isArray(events.events) ? events.events : [],
        eventsAvailability: events.availability ?? null,
        episodes: Array.isArray(idle.episodes) ? idle.episodes : [],
        policy: idle.policy || null,
        // Keyed by deviceSerial. Absent on a server that predates the per-asset preset work, which
        // reads as "only the single `policy` block is available" rather than as an empty fleet.
        policyByAsset: idle.policyByAsset || null,
        rollup: idle.rollup || null,
        idleAvailability: idle.availability ?? null,
        fuelTrips: Array.isArray(fuel.trips) ? fuel.trips : [],
        fuelEvents: Array.isArray(fuel.events) ? fuel.events : [],
        fuelAvailability: fuel.availability ?? null,
        chargeSessions: Array.isArray(charge.sessions) ? charge.sessions : [],
        chargeAvailability: charge.availability ?? null,
        ledgerEntries: Array.isArray(ledger.entries) ? ledger.entries : [],
        ledgerAssets: Array.isArray(ledger.assets) ? ledger.assets : [],
        ledgerAvailability: ledger.availability || null,
        ledgerRollup: ledger.rollup || null,
        speedTrips: Array.isArray(speed.trips) ? speed.trips : [],
        tooLarge: false,
        error: null,
    };
};

/**
 * Fetch analytics for the same assets as the trip fan-out.
 *
 * `deviceOid` is optional on this endpoint, but the panel's selection is 1..10 assets and
 * there is no multi-device parameter — one agency-wide call would return assets the user
 * did not pick and would hit the server's telemetry row cap far sooner than a scoped one.
 */
export const fetchAnalyticsForAssets = async (assets, options) => {
    const {
        token, agencyId, fromTime, toTime,
    } = options || {};

    const queue = requestQueue(assets);
    if (queue.length === 0) return [];

    return mapWithConcurrency(queue, async (asset) => {
        try {
            const raw = await APIGetTripAnalytics(() => {}, token, agencyId, fromTime, toTime, asset.uuid);
            return { asset, ...normaliseAnalyticsResponse(raw) };
        } catch (error) {
            return { asset, ...EMPTY_ANALYTICS, error: extractErrorMessage(error) };
        }
    });
};

// ---- per-trip trail (GET /v3/trip/trail) ------------------------------------------

const EMPTY_TRAIL = {
    trip: null,
    samples: [],
    sampleCount: 0,
    cadenceSec: null,
    availability: null,
    tooLarge: false,
    notFound: false,
};

/**
 * Normalise one APIGetTripTrail result for the trip tape.
 *
 * Same two quirks as the other two fetches — an AxiosError arrives in the success position, and the
 * real status is in the envelope rather than the HTTP code — plus two of its own:
 *
 *  - **404 is an answer, not a failure.** The trip is gone, or was never this agency's. The tape
 *    says so rather than showing a spinner or a generic error.
 *  - **A 200 with `samples: []` is also an answer**: this trip carried no telemetry. The endpoint
 *    never sends 204 precisely so this case can carry a body.
 */
export const normaliseTrailResponse = (result) => {
    if (result instanceof Error || !result?.data) {
        return { ...EMPTY_TRAIL, error: extractErrorMessage(result) };
    }

    const { status } = result.data;

    if (status === 404) {
        return { ...EMPTY_TRAIL, notFound: true, error: null };
    }

    if (status === 413) {
        return { ...EMPTY_TRAIL, tooLarge: true, error: extractErrorMessage(result) };
    }

    if (status !== 200) {
        return { ...EMPTY_TRAIL, error: extractErrorMessage(result) };
    }

    const payload = result.data.data || {};
    return {
        trip: payload.trip || null,
        samples: Array.isArray(payload.samples) ? payload.samples : [],
        sampleCount: Number(payload.sampleCount) || 0,
        cadenceSec: payload.cadenceSec ?? null,
        availability: payload.availability || null,
        tooLarge: false,
        notFound: false,
        error: null,
    };
};

/** One trip's trail. Resolves — never rejects — so the tape can render its own failure. */
export const fetchTrailForTrip = async (trip, options) => {
    const { token, agencyId } = options || {};
    if (!trip?.startTraceId) return { ...EMPTY_TRAIL, error: null };

    try {
        const raw = await APIGetTripTrail(
            () => {}, token, agencyId, trip.startTraceId, trip.startTime,
        );
        return normaliseTrailResponse(raw);
    } catch (error) {
        return { ...EMPTY_TRAIL, error: extractErrorMessage(error) };
    }
};

// 'none' is the idle engine's word for a missing ambient reading; 'absent' is the
// availability vocabulary everywhere else.
const ABSENT_TOKENS = new Set(['absent', 'none']);

/**
 * Fold per-asset availability into one verdict. Availability is a string for events and a
 * per-signal object for idle (`{ ambient: 'measured' }`), so both are flattened to their
 * string values. 'absent' only when every asset that answered reported nothing measured —
 * one OBD-equipped asset in a mixed fleet still makes the signal available.
 */
export const mergeAvailability = (values) => {
    const answered = (values || []).filter((value) => value !== null && value !== undefined);
    if (answered.length === 0) return null;

    const flat = answered.flatMap((value) => (typeof value === 'string'
        ? [value]
        : Object.values(value).filter((entry) => typeof entry === 'string')));

    if (flat.length === 0) return null;
    return flat.every((value) => ABSENT_TOKENS.has(value)) ? 'absent' : 'available';
};

/**
 * Fold the ledger's three-part availability, one key at a time and in its own vocabulary.
 *
 * Deliberately not `mergeAvailability`, twice over. That helper flattens an object to one verdict,
 * which is right for idle's per-signal block and wrong here — `{fuel, charge, energyCounter}` are
 * three independent questions, and collapsing them would report an ICE fleet's missing charge
 * signal as its fuel signal missing too. It also answers only `available`/`absent`, and the whole
 * point of this section's vocabulary is that `unpopulated` (the column exists, the device never
 * filled it) and `blocked` (percentage read with no tank capacity to scale it) are different
 * answers a reader can act on. Best verdict across the fan-out wins: one OBD asset in a mixed
 * fleet still makes the signal available.
 */
const AVAILABILITY_RANK = ['absent', 'unpopulated', 'blocked', 'available'];

const bestAvailability = (values) => (values || [])
    .filter((value) => AVAILABILITY_RANK.includes(value))
    .reduce(
        (best, value) => (AVAILABILITY_RANK.indexOf(value) > AVAILABILITY_RANK.indexOf(best)
            ? value : best),
        null,
    );

const mergeLedgerAvailability = (blocks) => {
    const present = (blocks || []).filter(Boolean);
    if (present.length === 0) return null;
    return {
        fuel: bestAvailability(present.map((block) => block.fuel)),
        charge: bestAvailability(present.map((block) => block.charge)),
        energyCounter: bestAvailability(present.map((block) => block.energyCounter)),
    };
};

/**
 * The one idle policy the whole fan-out shares, or null when the assets disagree.
 *
 * Compared by value, not identity: each asset's response carries its own object, so two assets on
 * the same vehicle preset produce equal-but-distinct blocks.
 */
const samePolicy = (policies) => {
    const present = (policies || []).filter(Boolean);
    if (present.length === 0) return null;
    const [first] = present;
    const reference = JSON.stringify(first);
    return present.every((policy) => JSON.stringify(policy) === reference) ? first : null;
};

/**
 * Fold every asset's own idle thresholds into one map, keyed by the name the tables show.
 *
 * The server keys `policyByAsset` by `deviceSerial`, which is the serial on the tracker rather than
 * anything a fleet manager recognises. Each fan-out entry covers exactly one asset, so its own
 * `asset.id` is the display name for whatever came back — the serial is the fallback for an entry
 * whose asset record is missing.
 *
 * Null when no asset reported one, so callers can tell "the server does not send this" from "the
 * fleet agreed", which are different reasons for a note to say nothing.
 */
const mergePolicyByAsset = (results) => {
    const merged = {};

    (results || []).forEach((entry) => {
        Object.entries(entry?.policyByAsset || {}).forEach(([serial, policy]) => {
            merged[entry.asset?.id || serial] = policy;
        });
    });

    return Object.keys(merged).length > 0 ? merged : null;
};

/**
 * Sum the per-asset rollups. Every rollup field is an additive count of seconds or
 * episodes, so this adds numeric keys without needing to know their names.
 */
const sumRollups = (rollups) => {
    const present = (rollups || []).filter(Boolean);
    if (present.length === 0) return null;

    const totals = {};
    present.forEach((rollup) => {
        Object.entries(rollup).forEach(([key, value]) => {
            if (typeof value === 'number') totals[key] = (totals[key] || 0) + value;
        });
    });
    return totals;
};

/** Flatten the analytics fan-out into the shapes SafetyView and IdleView take. */
export const mergeAnalytics = (results) => {
    const list = (Array.isArray(results) ? results : []).filter(Boolean);

    // Both tables mix assets, and neither column set carried an Asset column before this.
    // The API sends assetName/deviceSerial on every row; the asset is the fallback.
    const stamp = (rows, asset) => (rows || []).map((row) => ({
        ...row,
        assetName: row.assetName || asset?.id || '-',
        deviceSerial: row.deviceSerial || asset?.name || '',
    }));

    // Renumbered across the fan-out, like ledgerEntries below: each asset's response numbers its
    // own rows from 1, and the Route Map resolves a clicked dot by id alone — colliding ids pinned
    // the first asset's row, with the wrong popup at the wrong coordinates.
    const renumber = (rows) => rows.map((row, index) => ({ ...row, id: index + 1 }));

    return {
        // True only when at least one asset's analytics request came back. With the telemetry-engines
        // flag off nothing is fetched, and a total failure returns only errors — in both cases every
        // list below is `[]`, which is truthy, so callers probing the payload cannot tell "nothing to
        // report" from "nobody looked" and render a confident zero for both.
        loaded: list.some((entry) => !entry?.error),
        events: renumber(list.flatMap((entry) => stamp(entry.events, entry.asset))),
        eventsAvailability: mergeAvailability(list.map((entry) => entry.eventsAvailability)),
        episodes: renumber(list.flatMap((entry) => stamp(entry.episodes, entry.asset))),
        // Idle thresholds come from each asset's own vehicle preset, so a fan-out over a mixed
        // fleet carries several. Only reported when they agree — one sentence cannot honestly
        // state "idling past 5m counts as excessive" for a fleet where it is 10m on the trucks,
        // and a note the reader cannot trust is worse than no note.
        policy: samePolicy(list.map((entry) => entry.policy)),
        // What the fleet actually ran under, asset by asset. This is what the disagreeing case
        // needs: `policy` above can only go silent, which left the reader with no way to find out
        // which thresholds produced the numbers in front of them.
        policyByAsset: mergePolicyByAsset(list),
        rollup: sumRollups(list.map((entry) => entry.rollup)),
        idleAvailability: mergeAvailability(list.map((entry) => entry.idleAvailability)),
        // Keyed by tripId downstream, so no asset stamping is needed — unlike events and episodes,
        // these rows are never rendered on their own.
        fuelTrips: list.flatMap((entry) => entry.fuelTrips || []),
        // Stamped, because the Overview attributes drains per asset row.
        fuelEvents: renumber(list.flatMap((entry) => stamp(entry.fuelEvents, entry.asset))),
        fuelAvailability: mergeAvailability(list.map((entry) => entry.fuelAvailability)),
        // Stamped like the other per-row lists: the map scopes overlays to the picked asset.
        chargeSessions: renumber(list.flatMap((entry) => stamp(entry.chargeSessions, entry.asset))),
        chargeAvailability: mergeAvailability(list.map((entry) => entry.chargeAvailability)),
        // Re-sorted and renumbered across the fan-out: each asset's response numbers its own
        // entries from 1, and the table keys on `id`. The running totals are untouched — the
        // server accumulates them per device, which is the one thing a merge here must not redo.
        ledgerEntries: list
            .flatMap((entry) => stamp(entry.ledgerEntries, entry.asset))
            .sort((left, right) => Date.parse(left.startTime) - Date.parse(right.startTime)
                || String(left.deviceSerial).localeCompare(String(right.deviceSerial)))
            .map((row, index) => ({ ...row, id: index + 1 })),
        ledgerAssets: list.flatMap((entry) => entry.ledgerAssets || []),
        // Per key rather than one verdict: the view shows fuel tiles or energy tiles off these, and
        // an ICE fleet's `charge: unpopulated` must not drag its litres down with it.
        ledgerAvailability: mergeLedgerAvailability(list.map((entry) => entry.ledgerAvailability)),
        ledgerRollup: sumRollups(list.map((entry) => entry.ledgerRollup)),
        // Also keyed by tripId, so the asset filter comes free — no stamping, no day-level scoping.
        speedTrips: list.flatMap((entry) => entry.speedTrips || []),
        errors: list.filter((entry) => entry.error)
            .map((entry) => ({ name: entry.asset?.id || '-', error: entry.error })),
        tooLarge: list.some((entry) => entry.tooLarge),
    };
};

/** Flatten a fan-out into one trip list, stamping each trip with its asset for grouping. */
export const flattenTrips = (results) => (Array.isArray(results) ? results : [])
    .flatMap((result) => (result?.trips || []).map((trip) => ({
        ...trip,
        // tripList rows already carry assetName, but a null slips through on odd exports
        // and every group-by downstream keys on it.
        assetName: trip.assetName || result.asset?.id || '-',
        deviceSerial: trip.deviceSerial || result.asset?.name || '',
    })));

/** Assets that came back with an error, for the snackbar summary. */
export const failedAssets = (results) => (Array.isArray(results) ? results : [])
    .filter((result) => result?.error)
    .map((result) => ({ name: result.asset?.id || '-', error: result.error }));
